import React, { useRef, useState } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { FiCamera, FiImage } from 'react-icons/fi'
import CameraCaptureModal from './CameraCaptureModal'

const ImageSourceUploadButton = ({
  inputId,
  accept = 'image/*',
  captureFacing = 'environment',
  onFileSelect,
  children,
  className = '',
  disabled = false,
}) => {
  const galleryInputRef = useRef(null)
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [showCameraModal, setShowCameraModal] = useState(false)

  const handleFileChange = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
    event.target.value = ''
    setShowSourceModal(false)
  }

  const openGallery = () => {
    setShowSourceModal(false)
    requestAnimationFrame(() => {
      galleryInputRef.current?.click()
    })
  }

  const openCamera = () => {
    setShowSourceModal(false)
    setShowCameraModal(true)
  }

  const handleCameraCapture = (file) => {
    setShowCameraModal(false)
    onFileSelect(file)
  }

  const handleTriggerClick = (event) => {
    if (disabled) return
    event.preventDefault()
    event.stopPropagation()
    setShowSourceModal(true)
  }

  return (
    <>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleTriggerClick}
        onKeyDown={(event) => {
          if (disabled) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setShowSourceModal(true)
          }
        }}
        className={className}
        aria-disabled={disabled}
      >
        {children}
      </div>

      <input
        ref={galleryInputRef}
        type="file"
        id={`${inputId}-gallery`}
        className="hidden"
        accept={accept}
        onChange={handleFileChange}
      />

      <CameraCaptureModal
        isOpen={showCameraModal}
        onClose={() => setShowCameraModal(false)}
        onCapture={handleCameraCapture}
        facingMode={captureFacing}
      />

      {showSourceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md p-4">
          <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full max-w-[360px] p-5 relative">
            <button
              type="button"
              onClick={() => setShowSourceModal(false)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <AiOutlineCloseCircle className="h-6 w-6" />
            </button>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 pr-8">
              Add image
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Choose how you want to add this image.
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={openGallery}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 dark:border-border-dark hover:bg-gray-50 dark:hover:bg-secondaryDark text-left"
              >
                <span className="w-10 h-10 rounded-full bg-yellowcolor/20 flex items-center justify-center shrink-0">
                  <FiImage className="text-yellowcolor" size={20} />
                </span>
                <span>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">
                    Select from gallery
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Choose an existing photo
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={openCamera}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 dark:border-border-dark hover:bg-gray-50 dark:hover:bg-secondaryDark text-left"
              >
                <span className="w-10 h-10 rounded-full bg-yellowcolor/20 flex items-center justify-center shrink-0">
                  <FiCamera className="text-yellowcolor" size={20} />
                </span>
                <span>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">
                    Take a picture
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Use your device camera
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ImageSourceUploadButton
